// Shared display helpers for BriefCard and BriefDetailDialog — kept in sync
// with the STATUS_OPTIONS/PRIORITY_OPTIONS enums in briefOptions.js.

export const PRIORITY_COLORS = {
  High: "#EF4444",
  Medium: "#F59E0B",
  Low: "#10B981",
};

export const STATUS_COLORS = {
  Pending: { bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B" },
  "In Progress": { bg: "#DBEAFE", color: "#1E40AF", dot: "#3B82F6" },
  Approved: { bg: "#D1FAE5", color: "#065F46", dot: "#10B981" },
  Delayed: { bg: "#FEE2E2", color: "#991B1B", dot: "#EF4444" },
};

// Display-only relabeling — "Approved" stays the real stored/queried enum
// value (models/brief.js's STATUS_OPTIONS in pluginbackendnode, the tab's
// filter param, Joi validation) so this is purely cosmetic and doesn't touch
// any stored data, API contract, or the two of them kept in sync. Falls back
// to the raw status for anything not explicitly relabeled.
export const STATUS_LABELS = {
  Approved: "Completed",
};
export const labelForStatus = (status) => STATUS_LABELS[status] || status;

const AVATAR_PALETTE = ["#FF5200", "#7C3AED", "#0EA5E9", "#F59E0B", "#10B981", "#EC4899"];

export const colorForName = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

export const initialsFor = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";

export const formatBudget = (budget) => (budget != null ? Number(budget).toLocaleString("en-IN") : "—");

// Distinct from formatDate (plans/formatDate.js) on purpose — a brief's
// deadline is day-granular and correctly shows no time-of-day, but an
// activity feed of same-day comments needs one or they're all
// indistinguishable ("21 Aug", "21 Aug", "21 Aug", ...).
export const formatCommentTimestamp = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "Asia/Kolkata",
    });
};

// S3 keys look like ".../brief-attachments/{briefId}/{uuid}-{originalName}"
// (see pluginbackendnode's helpers/s3BriefAttachments.js) — strip the path and
// the uuid prefix so the chip shows something a human recognizes.
export const attachmentFileName = (url) => {
    try {
        const last = decodeURIComponent(url.split('/').pop() || '');
        return last.replace(/^[0-9a-f-]{36}-/i, '') || last;
    } catch {
        return url;
    }
};

// @{{userId}} tokens embedded in a comment body (see pluginbackendnode's
// helpers/mentionParser.js) — ID-only, so splitting a comment's body and
// resolving each id's display name from that same comment's own populated
// `mentions` array (not a separate global lookup) keeps rendering
// self-contained per comment.
const MENTION_TOKEN_REGEX = /@\{\{([0-9a-fA-F]{24})\}\}/g;

const URL_REGEX = /https?:\/\/[^\s]+/g;
// A URL followed by a sentence-ending/bracketing character shouldn't swallow
// it — "check https://x.com." should link "https://x.com", not
// "https://x.com." — but only trims from the end, so a URL's own trailing
// slash or a closing paren that's actually part of the URL is left alone
// unless it's one of these specific "probably not part of the URL" chars.
const TRAILING_PUNCTUATION_REGEX = /[.,!?;:'")\]]+$/;

// Splits plain text into {text} and {url} pieces — a second pass applied to
// splitCommentBody's own {text} pieces, so a link typed alongside an
// @mention still renders clickable.
export const linkifyText = (text) => {
    const pieces = [];
    let lastIndex = 0;
    for (const match of String(text || '').matchAll(URL_REGEX)) {
        let url = match[0];
        const trailingMatch = url.match(TRAILING_PUNCTUATION_REGEX);
        const trailing = trailingMatch ? trailingMatch[0] : '';
        if (trailing) url = url.slice(0, url.length - trailing.length);
        if (!url) continue; // the "URL" was entirely punctuation — not a real link

        if (match.index > lastIndex) pieces.push({ text: text.slice(lastIndex, match.index) });
        pieces.push({ url });
        lastIndex = match.index + url.length;
        if (trailing) {
            pieces.push({ text: trailing });
            lastIndex += trailing.length;
        }
    }
    if (lastIndex < text.length) pieces.push({ text: text.slice(lastIndex) });
    return pieces;
};

// Returns an array of {text}, {mentionId, name}, and {url} pieces, in order,
// ready for a caller to map into plain text vs. styled mention chips vs.
// real clickable links.
export const splitCommentBody = (body, mentions = []) => {
    const nameById = new Map(mentions.map((m) => [m.id, m.name]));
    const mentionPieces = [];
    let lastIndex = 0;
    for (const match of String(body || '').matchAll(MENTION_TOKEN_REGEX)) {
        if (match.index > lastIndex) mentionPieces.push({ text: body.slice(lastIndex, match.index) });
        const id = match[1];
        mentionPieces.push({ mentionId: id, name: nameById.get(id) || 'Unknown' });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < body.length) mentionPieces.push({ text: body.slice(lastIndex) });

    return mentionPieces.flatMap((piece) => (piece.text != null ? linkifyText(piece.text) : [piece]));
};

// Inverse direction — building a comment body from picked mentions + typed
// text, for the composer.
export const buildCommentBody = (mentionedUsers, text) => {
    const tokens = mentionedUsers.map((u) => `@{{${u.id}}}`).join(' ');
    return [tokens, text.trim()].filter(Boolean).join(' ');
};
